import { partial } from 'lodash-es';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useState } from 'react';

import type { IBrowserStoragePersistenceOptions } from './types';
import {
  BrowserStorageType,
  clearBrowserStoragePayload,
  getValidBrowserStoragePayload,
  setBrowserStoragePayload,
} from './utils';

// Based on https://usehooks.com/useLocalStorage/
function useBrowserStorageState<T>(
  browserStorageType: BrowserStorageType,
  key: string,
  initialValue: T,
  options: IBrowserStoragePersistenceOptions = {}
): [T, Dispatch<SetStateAction<T>>, () => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    return getValidBrowserStoragePayload<T>(browserStorageType, key, initialValue, options);
  });

  const setValue = useCallback(
    (param: SetStateAction<T>) => {
      if (typeof param === 'function') {
        setStoredValue((currentValue) => {
          const newValue = (param as (v: T) => T)(currentValue);
          setBrowserStoragePayload(browserStorageType, key, newValue);
          return newValue;
        });
      } else {
        setStoredValue(param);
        setBrowserStoragePayload(browserStorageType, key, param);
      }
    },
    [key, browserStorageType]
  );

  const clearStorage = useCallback(() => {
    clearBrowserStoragePayload(browserStorageType, key);
  }, [key, browserStorageType]);

  return [storedValue, setValue, clearStorage];
}

type UseStorageStateType = <T>(
  key: string,
  initialValue: T,
  options?: IBrowserStoragePersistenceOptions
) => [T, Dispatch<SetStateAction<T>>, () => void];

export const useLocalStorageState: UseStorageStateType = partial(
  useBrowserStorageState,
  BrowserStorageType.LOCAL_STORAGE
);

export const useSessionStorageState: UseStorageStateType = partial(
  useBrowserStorageState,
  BrowserStorageType.SESSION_STORAGE
);
